import React, { useEffect, useState } from 'react';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { formatDateTime, formatRand } from '@/utility/helper';
import CommonTable, { TableColumn } from '@/components/common/CommonTable';
import Pagination from '@/components/common/Pagination';
import { IPageInfo } from '@/interfaces/common.interface';
import { IOrder, IOrderResponse } from '@/interfaces/Order/order.interface';
import { SectionCard } from '@/components/common/SectionCard';
import { Calendar, Hash, Truck, Eye } from 'lucide-react';
import { RoutesLink } from '@/utility/RouteLinks';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';
import { StatusBadge } from '@/components/common/StatusBadge';

interface DriverDeliveredOrdersProps {
    driverId: string;
}

export const DriverDeliveredOrders: React.FC<DriverDeliveredOrdersProps> = ({ driverId }) => {
    const navigate = useRoleNavigate();
    const [orders, setOrders] = useState<IOrder[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageInfo, setPageInfo] = useState<IPageInfo>({ page: 1, limit: 10, total: 0, totalPages: 0 });

    const fetchOrders = async (page = 1) => {
        setLoading(true);
        try {
            const response = await callApi<IOrderResponse>(
                `${API_URL}${API_ENDPOINT.orders}?driverId=${driverId}&status=delivered&page=${page}&limit=${pageInfo.limit}`,
                { method: 'GET' }
            );

            if (response?.data) {
                setOrders(response?.data?.order || []);
                setPageInfo(response?.data?.pageInfo || { limit: 10, page: 1, total: 0, totalPages: 0 });
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (driverId) {
            fetchOrders(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [driverId]);

    const columns: TableColumn<IOrder>[] = [
        {
            key: 'orderId',
            header: 'Order / Ref ID',
            render: (item) => (
                <div
                    className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => navigate(RoutesLink.ViewOrders.replace(':id', item.id))}
                >
                    <Hash className="w-3 h-3 text-muted-foreground" />
                    <span className="font-mono text-sm font-semibold text-primary underline underline-offset-2">
                        {item.orderId}
                    </span>
                </div>
            )
        },
        {
            key: 'date',
            header: 'Date',
            render: (item) => (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatDateTime(item.createdAt)}
                </div>
            )
        },
        {
            key: 'rideId',
            header: 'Delivery Type',
            render: (item) => {
                const isSingle = !item.rideId;
                return (
                    <span
                        className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${isSingle
                            ? 'bg-slate-100 text-slate-700 border border-slate-200/80'
                            : 'bg-purple-100 text-purple-700 border border-purple-200/80'
                            }`}
                    >
                        {isSingle ? 'Single' : 'Multiple'}
                    </span>
                );
            }
        },
        {
            key: 'status',
            header: 'Status',
            render: (item) => <StatusBadge status={item.status} />
        },
        {
            key: 'amount',
            header: 'Earning Amount',
            render: (item) => {
                const earning = item.driverEarning ? Number(item.driverEarning.earning) : 0;
                return (
                    <div className="font-bold text-green-600">
                        {formatRand(earning)}
                    </div>
                );
            }
        }
    ];

    const actions = [
        {
            text: 'View Order',
            icon: <Eye size={16} strokeWidth={1.5} className="cursor-pointer text-muted-foreground hover:text-primary transition-colors" />,
            onClick: (record: IOrder) => {
                navigate(RoutesLink.ViewOrders.replace(':id', record.id));
            },
        }
    ];

    return (
        <SectionCard title="Delivered Orders & Earnings" icon={<Truck className="w-5 h-5 text-primary" />}>
            <div className="space-y-4">
                <CommonTable
                    columns={columns}
                    data={orders}
                    loading={loading}
                    emptyMessage="No delivered orders found for this driver."
                    actions={actions}
                />

                {pageInfo.totalPages > 1 && (
                    <Pagination
                        currentPage={pageInfo.page}
                        totalPages={pageInfo.totalPages}
                        onPageChange={fetchOrders}
                    />
                )}
            </div>
        </SectionCard>
    );
};
